import { eachDayOfInterval, endOfMonth, startOfMonth } from 'date-fns';
import { omit } from 'lodash-es';
import cn from 'classnames';
import { ShiftSiblingData, ResourceShiftTotalsMap } from '@lp/features/Scheduler/types';
import { isDifferentHrSource, toZonedISODateKey } from '@lp/features/Scheduler/utils';
import {
  OMSResource,
  OMSShift,
  OMSShiftSelectPayload,
  OMSShiftSlot,
  OMSShiftSlotPayload,
  OMSShiftType,
  SetOMSShiftData,
  ShiftStatus,
  OMSResourcesWithShifts,
} from '@/api/apis/resources/schedule';
import { OMSShiftOptionType } from './types';
import {
  omsShiftOptionToTypeMap,
  omsShiftTypeNames,
  singleOMSShiftTypes,
  slotSelectableOMSShiftTypes,
} from './constants';

export const getOMSSiblingData = (shift: OMSShift, prevShift?: OMSShift): ShiftSiblingData => {
  const shiftType = shift.slot1?.shiftType || shift.slot2?.shiftType || null;
  const prevShiftType = prevShift?.slot1?.shiftType || prevShift?.slot2?.shiftType || null;
  const isSingle = singleOMSShiftTypes.includes(shiftType as OMSShiftType);
  const sameShiftType = !!shiftType && shiftType === prevShiftType;

  if (isSingle || !sameShiftType) return { hasPrev: false };

  if (isDifferentHrSource(shift, prevShift)) return { hasPartialPrev: true };

  return { hasPrev: true };
};

export const getOMSShiftSetData = (payload: OMSShiftSelectPayload, resource: OMSResource): SetOMSShiftData => {
  const { slotsPerDay, slot1: payloadSlot1, slot2: payloadSlot2 } = payload;

  const mapPayloadToSlot = (
    ps: OMSShiftSlotPayload | null | undefined,
    index: number,
  ): OMSShiftSlot | null | undefined => {
    if (!ps) return ps;
    return {
      slotIndex: index,
      ofSlotsPerDay: slotsPerDay,
      shiftType: ps.shiftType,
      isVoluntary: !!ps.isVoluntary,
      organs: ps.shiftType === OMSShiftType.OnCall ? resource.organs : null,
    };
  };

  const slot1 = mapPayloadToSlot(payloadSlot1, 1);
  const slot2 = mapPayloadToSlot(payloadSlot2, 2);

  return { slotsPerDay, slot1, slot2 };
};

export const getShiftSlotFromShiftOption = (shiftOption?: OMSShiftOptionType | null) => {
  if (!shiftOption) return undefined;

  switch (shiftOption) {
    case OMSShiftOptionType.OnCall:
      return { shiftType: OMSShiftType.OnCall, isVoluntary: false };
    case OMSShiftOptionType.OnCallVoluntary:
      return { shiftType: OMSShiftType.OnCall, isVoluntary: true };
    default:
      return { shiftType: OMSShiftType[shiftOption] };
  }
};

export const getShiftOptionFromShiftSlot = (slot?: OMSShiftSlot | null) => {
  if (!slot) return undefined;
  switch (true) {
    case slot.shiftType === OMSShiftType.OnCall && slot.isVoluntary:
      return OMSShiftOptionType.OnCallVoluntary;
    default:
      return OMSShiftOptionType[OMSShiftType[slot.shiftType]];
  }
};

export const getOMSResourceShiftTotalsMap = (selectedDay: Date, shiftsData: OMSResourcesWithShifts) => {
  const resourceShiftTotalsMap: ResourceShiftTotalsMap<OMSShiftOptionType> = {};
  const dates = eachDayOfInterval({ start: startOfMonth(selectedDay), end: endOfMonth(selectedDay) });
  const dayKeys = dates.map(toZonedISODateKey);

  Object.values(shiftsData).forEach((data) => {
    if (data.resource) {
      const totals: Partial<Record<OMSShiftOptionType, number>> = {};

      dayKeys.forEach((key) => {
        const shift = data.days[key];
        if (shift) {
          if (shift.slot1) {
            const type = getShiftOptionFromShiftSlot(shift.slot1) as OMSShiftOptionType;
            totals[type] = (totals[type] || 0) + 1;
          }
          if (shift.slot2) {
            const type = getShiftOptionFromShiftSlot(shift.slot2) as OMSShiftOptionType;
            totals[type] = (totals[type] || 0) + 1;
          }
        }
      });

      resourceShiftTotalsMap[data.resource.id] = totals;
    }
  });

  return resourceShiftTotalsMap;
};

export const omsUserRowsFilterComparator = (filter: Set<OMSShiftType>, shift?: OMSShift) => {
  if (filter.size === 0) return true;
  if (!shift) return false;

  const { slot1, slot2 } = shift;

  const slot1Match = !!slot1 && filter.has(slot1.shiftType);
  const slot2Match = !!slot2 && filter.has(slot2.shiftType);

  return slot1Match || slot2Match;
};

export const getOMSShiftSlotColors = (
  shiftSlot?: OMSShiftSlot | null,
  shiftStatus?: ShiftStatus,
  isPreSchedule?: boolean,
) => {
  const { shiftType, isVoluntary } = shiftSlot ?? {};
  const base = {
    bg: 'bg-transparent',
    text: 'text-base-25',
    secondaryText: 'text-base-70',
    border: 'border-transparent',
    icon: 'text-base-5',
  };

  if (!shiftType) return base;

  const isPlanning = shiftStatus === ShiftStatus.InPlanningProcess;
  const borderStyle = isPreSchedule ? 'border' : 'border border-dashed';

  if (isPlanning || isPreSchedule) {
    switch (shiftType) {
      case OMSShiftType.OnCall:
        return {
          bg: isVoluntary ? 'bg-success-30/30' : 'bg-success-40/20',
          text: 'text-success-5',
          secondaryText: 'text-success-20',
          border: cn(borderStyle, 'border-success-10'),
          icon: 'text-success-40',
        };
      case OMSShiftType.AdminDay:
        return {
          bg: 'bg-info-40/20',
          text: 'text-info-20',
          secondaryText: 'text-info-20',
          border: cn(borderStyle, 'border-info-20'),
          icon: 'text-info-40',
        };
      case OMSShiftType.NoCall:
        return {
          bg: 'bg-transparent',
          text: 'text-base-25',
          secondaryText: 'text-base-40',
          border: cn(borderStyle, 'border-base-20'),
          icon: 'text-base-30',
        };
      case OMSShiftType.SickLeave:
      case OMSShiftType.Vacation:
        return {
          bg: 'bg-warning-40/20',
          text: 'text-warning-5',
          secondaryText: 'text-warning-5',
          border: 'border-transparent',
          icon: 'text-warning-20',
        };
      case OMSShiftType.Unavailable:
        return {
          bg: 'bg-danger-40/10',
          text: 'text-danger-5',
          secondaryText: 'text-danger-20',
          border: 'border border-danger-20',
          icon: 'text-danger-30',
        };
      default:
        return base;
    }
  }

  switch (shiftType) {
    case OMSShiftType.OnCall:
      return {
        bg: isVoluntary ? 'bg-success-30' : 'bg-success-40',
        text: 'text-white',
        secondaryText: 'text-success-70',
        border: 'border-transparent',
        icon: 'text-success-40',
      };
    case OMSShiftType.AdminDay:
      return {
        bg: 'bg-info-40',
        text: 'text-white',
        secondaryText: 'text-base-5',
        border: 'border-transparent',
        icon: 'text-info-20',
      };
    case OMSShiftType.SickLeave:
    case OMSShiftType.Vacation:
      return {
        bg: 'bg-warning-40/20',
        text: 'text-warning-5',
        secondaryText: 'text-warning-5',
        border: 'border-transparent',
        icon: 'text-warning-20',
      };
    case OMSShiftType.Unavailable:
      return {
        bg: 'bg-danger-40/20',
        text: 'text-danger-5',
        secondaryText: 'text-danger-5',
        border: 'border-transparent',
        icon: 'text-danger-30',
      };
    case OMSShiftType.NoCall:
    default:
      return base;
  }
};

export const getShiftOptionIconColor = (optionType: OMSShiftOptionType): string => {
  switch (optionType) {
    case OMSShiftOptionType.OnCall:
      return 'text-success-40';
    case OMSShiftOptionType.OnCallVoluntary:
      return 'text-success-30';
    case OMSShiftOptionType.AdminDay:
      return 'text-info-40';
    case OMSShiftOptionType.NoCall:
      return 'text-base-30';
    case OMSShiftOptionType.SickLeave:
      return 'text-warning-50';
    case OMSShiftOptionType.Vacation:
      return 'text-warning-40/20';
    case OMSShiftOptionType.Unavailable:
      return 'text-danger-40';
    default:
      return 'text-base-5';
  }
};

export const getOMSShiftSlotPrimaryText = (slot?: OMSShiftSlot | null): string => {
  if (!slot) return '';
  return omsShiftTypeNames[slot.shiftType] ?? '';
};

export const getOMSShiftSlotSecondaryText = (slot?: OMSShiftSlot | null): string | null => {
  if (!slot) return null;
  if (slot.shiftType === OMSShiftType.OnCall && slot.isVoluntary) return 'Voluntary';
  return null;
};

export const isDualSlotShiftType = (shiftType?: OMSShiftType): boolean => {
  if (!shiftType) return false;
  return slotSelectableOMSShiftTypes.includes(shiftType);
};

export const isDualSlotOptionType = (optionType?: OMSShiftOptionType): boolean => {
  if (!optionType) return false;
  const shiftType = omsShiftOptionToTypeMap[optionType];
  return isDualSlotShiftType(shiftType);
};

export const isOnCallOption = (optionType?: OMSShiftOptionType | null): boolean =>
  optionType === OMSShiftOptionType.OnCall || optionType === OMSShiftOptionType.OnCallVoluntary;

export const isAdminDayOption = (optionType?: OMSShiftOptionType | null): boolean =>
  optionType === OMSShiftOptionType.AdminDay;

/**
 * Resolves the resulting slot options when toggling a dual-slot option (On‑Call / Admin Day)
 * for a given set of desired slots. Enforces the coexistence rule:
 * On‑Call and Admin Day can only share a day as Admin Day (Slot 1) + On‑Call (Slot 2).
 * Admin Day has priority — selecting it removes a conflicting On‑Call slot.
 */
export const resolveDualSlotSelection = (
  current: { slot1?: OMSShiftOptionType | null; slot2?: OMSShiftOptionType | null },
  optionType: OMSShiftOptionType,
  desiredSlots: { slot1: boolean; slot2: boolean },
): { slot1: OMSShiftOptionType | null; slot2: OMSShiftOptionType | null } => {
  // Drop any existing single (non-dual) shift — a dual selection always replaces it.
  let slot1: OMSShiftOptionType | null = isDualSlotOptionType(current.slot1 ?? undefined)
    ? (current.slot1 as OMSShiftOptionType)
    : null;
  let slot2: OMSShiftOptionType | null = isDualSlotOptionType(current.slot2 ?? undefined)
    ? (current.slot2 as OMSShiftOptionType)
    : null;

  // Remove the toggled option from any slot it currently occupies, then apply the desired placement.
  if (slot1 === optionType) slot1 = null;
  if (slot2 === optionType) slot2 = null;
  if (desiredSlots.slot1) slot1 = optionType;
  if (desiredSlots.slot2) slot2 = optionType;

  // Enforce the forbidden combination: On‑Call (Slot 1) + Admin Day (Slot 2).
  if (isOnCallOption(slot1) && isAdminDayOption(slot2)) {
    if (isAdminDayOption(optionType)) {
      slot1 = null; // Admin Day was selected — drop the conflicting On‑Call.
    } else {
      slot2 = null; // On‑Call was selected — drop Admin Day (also blocked in the UI).
    }
  }

  return { slot1, slot2 };
};

export const getIsLastOnCallSlotInDay = (
  shiftsData: OMSResourcesWithShifts,
  resourceId: string,
  day: string,
  slotIndex: number,
) => {
  const slotKey = slotIndex === 2 ? 'slot2' : 'slot1';
  const otherResourceShifts = omit(shiftsData, resourceId) as OMSResourcesWithShifts;
  const dayShifts = Object.values(otherResourceShifts).map((r) => r.days[day]?.[slotKey]);
  return !dayShifts.some((slot) => slot?.shiftType === OMSShiftType.OnCall);
};

const OMS_WD_SHIFT_TYPES = new Set([OMSShiftType.Unavailable, OMSShiftType.SickLeave, OMSShiftType.Vacation]);

export const getOMSShowHrIcon = (shiftData: OMSShift): boolean =>
  !!shiftData.isHrShift && !!shiftData.slot1?.shiftType && OMS_WD_SHIFT_TYPES.has(shiftData.slot1.shiftType);
